import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ShoppingBag, Ribbon, Coffee, Gift, Palette, MessageCircle } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
type Product = {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  image?: string;
  gallery?: string[];
  galleryStyle?: 'tumblers' | 'bows' | 'purses' | 'sets';
  galleryBadge?: {
    text: string;
    icon: React.ReactNode;
  };
  desc1: string;
  desc2?: string;
  feature?: string;
  featureIcon?: React.ReactNode;
  quoteQ?: string;
  quoteA?: string;
  questionLabel?: string;
  answerLabel?: string;
  sets?: {
    name: string;
    items: string;
  }[];
  tumblerBadgeText?: string;
  productSlug?: string;
};
const SHOP_BASE = 'https://shop.ferbystore.com';
export function CollectionSection() {
  const {
    t,
    lang
  } = useTranslation();
  const c = t.collection;
  const buildShopUrl = (slug?: string) => slug ? `${SHOP_BASE}/${lang}/products/${slug}` : `${SHOP_BASE}/`;
  const products: Product[] = [{
    id: 'mirrors',
    title: c.mirrors.title,
    subtitle: c.mirrors.subtitle,
    icon: <Sparkles className="w-5 h-5 text-lavender-600" data-id="element-30" />,
    image: 'https://cdn.magicpatterns.com/uploads/jHTgX6d1XTcGoayVj4gxup/Edit4.jpg',
    desc1: c.mirrors.desc1,
    desc2: c.mirrors.desc2,
    feature: c.mirrors.feature,
    featureIcon: <Palette className="w-5 h-5 text-lavender-500" data-id="element-31" />,
    productSlug: 'mirror'
  }, {
    id: 'purses',
    title: c.purses.title,
    subtitle: c.purses.subtitle,
    icon: <ShoppingBag className="w-5 h-5 text-blush-600" data-id="element-32" />,
    galleryStyle: 'purses',
    gallery: ['https://cdn.magicpatterns.com/uploads/inKRHT76haaLWjg3TQPaKQ/Edit14.png', 'https://cdn.magicpatterns.com/uploads/jDL7hMPVaDESJVXhBhY163/Edit41.png', 'https://cdn.magicpatterns.com/uploads/ihc8CM76fzfj1kFGfPFnFY/Edit39.png'],
    galleryBadge: {
      text: c.purses.badge,
      icon: <ShoppingBag className="w-3.5 h-3.5 text-blush-600" data-id="element-33" />
    },
    desc1: c.purses.desc1,
    quoteQ: c.purses.quoteQ,
    quoteA: c.purses.quoteA,
    questionLabel: c.purses.questionLabel,
    answerLabel: c.purses.answerLabel,
    productSlug: 'handmade-decorated-mini-handbag-doll-collection'
  }, {
    id: 'bows',
    title: c.bows.title,
    subtitle: c.bows.subtitle,
    icon: <Ribbon className="w-5 h-5 text-gold-600" data-id="element-34" />,
    galleryStyle: 'bows',
    gallery: ['https://cdn.magicpatterns.com/uploads/rhgGEC351p7rX7AvTyFGv1/Edit10.jpg', 'https://cdn.magicpatterns.com/uploads/oi3XJ6gWXoR6VyxuWA161j/Edit22.png', 'https://cdn.magicpatterns.com/uploads/mzkicFKt3r1mv3zt3hZUg6/Edit23.png', 'https://cdn.magicpatterns.com/uploads/i6QDMhSGJtopmpX4yy92jo/Edit24.png', 'https://cdn.magicpatterns.com/uploads/qFecx2E2awUNncQfKUFxbf/Edit26.png', 'https://cdn.magicpatterns.com/uploads/1hvzvQXULTe17x9eRfaUWR/Edit27.png'],
    galleryBadge: {
      text: c.bows.badge,
      icon: <Ribbon className="w-3.5 h-3.5 text-gold-600" data-id="element-35" />
    },
    desc1: c.bows.desc1,
    desc2: c.bows.desc2,
    productSlug: 'handmade-decorative-hair-bows-fairy-collection'
  }, {
    id: 'tumblers',
    title: c.tumblers.title,
    subtitle: c.tumblers.subtitle,
    icon: <Coffee className="w-5 h-5 text-lavender-600" data-id="element-36" />,
    galleryStyle: 'tumblers',
    gallery: ['https://cdn.magicpatterns.com/uploads/vjndBR1AuZ9QbJaPm8XGr1/Edit34.png', 'https://cdn.magicpatterns.com/uploads/iJTxrWPxz2kY9HLS9wonNd/Edit31.png', 'https://cdn.magicpatterns.com/uploads/2aAetUf4aKSfVnZUnfUz3N/Edit32.png', 'https://cdn.magicpatterns.com/uploads/jb7coF24re2QdzgmGW9fJM/Edit33.png'],
    desc1: c.tumblers.desc1,
    desc2: c.tumblers.desc2,
    tumblerBadgeText: c.tumblers.badge,
    productSlug: 'sublimation-tumbler-anime-collection'
  }, {
    id: 'sets',
    title: c.sets.title,
    subtitle: c.sets.subtitle,
    icon: <Gift className="w-5 h-5 text-blush-600" data-id="element-37" />,
    galleryStyle: 'sets',
    gallery: ['https://cdn.magicpatterns.com/uploads/fBDMTu4aZaTvo8spTeJkRp/Edit45.png', 'https://cdn.magicpatterns.com/uploads/7hPg8uXTnL4zUqP479fhws/Edit40.png', 'https://cdn.magicpatterns.com/uploads/hj32nnW3CyxbdMBXJjfcqR/Edit36.png', 'https://cdn.magicpatterns.com/uploads/mdipD758k1F2nubGKkgNkT/Edit8.jpg'],
    galleryBadge: {
      text: c.sets.badge,
      icon: <Gift className="w-3.5 h-3.5 text-blush-600" data-id="element-38" />
    },
    desc1: c.sets.desc1,
    sets: [{
      name: c.sets.girlsSet.name,
      items: c.sets.girlsSet.items
    }, {
      name: c.sets.womensSet.name,
      items: c.sets.womensSet.items
    }],
    desc2: c.sets.desc2,
    productSlug: 'handmade-decorative-hair-bows-fairy-collection'
  }];
  return <section id="collection" className="py-24 bg-cream-50" data-id="element-39">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-40">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-20" data-id="element-41">
          <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-4" data-id="element-42">
            {c.heading}
          </h2>
          <div className="w-24 h-1 bg-lavender-300 mx-auto rounded-full" data-id="element-43"></div>
        </motion.div>

        <div className="space-y-32" data-id="element-44">
          {products.map((product, index) => {
          const isEven = index % 2 === 0;
          return <div key={product.id} className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 lg:gap-20 items-center`} data-id="element-45">
                {/* Image / Gallery */}
                <motion.div initial={{
              opacity: 0,
              x: isEven ? -50 : 50,
              rotate: isEven ? -3 : 3
            }} whileInView={{
              opacity: 1,
              x: 0,
              rotate: 0
            }} viewport={{
              once: true,
              margin: '-100px'
            }} transition={{
              duration: 0.9,
              ease: [0.22, 1, 0.36, 1]
            }} className="w-full lg:w-1/2 relative" data-id="element-46">
                  {product.gallery && product.galleryStyle === 'tumblers' ? <TumblerFan product={product} isEven={isEven} data-id="element-47" /> : product.gallery && product.galleryStyle ? <BowScatter product={product} isEven={isEven} data-id="element-48" /> : <SingleImage product={product} isEven={isEven} data-id="element-49" />}

                  {/* Decorative blob */}
                  <div className={`absolute -z-10 w-72 h-72 rounded-full blur-3xl opacity-50 ${isEven ? '-bottom-10 -left-10 bg-lavender-300' : '-top-10 -right-10 bg-blush-300'}`} data-id="element-50"></div>

                  {/* Decorative rotating ring */}
                  <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 40,
                repeat: Infinity,
                ease: 'linear'
              }} className={`absolute -z-10 w-32 h-32 rounded-full border-2 border-dashed border-gold-500/30 ${isEven ? '-top-8 -right-8' : '-bottom-8 -left-8'}`} data-id="element-51"></motion.div>
                </motion.div>

                {/* Content */}
                <motion.div initial={{
              opacity: 0,
              x: isEven ? 50 : -50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true,
              margin: '-100px'
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} className="w-full lg:w-1/2" data-id="element-52">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-lavender-100 shadow-sm mb-6" data-id="element-53">
                    {product.icon}
                    <span className="text-sm font-medium text-gray-600 uppercase tracking-wider" data-id="element-54">
                      {product.subtitle}
                    </span>
                  </div>

                  <h3 className="font-serif text-3xl md:text-4xl text-gray-900 mb-6" data-id="element-55">
                    {product.title}
                  </h3>

                  <div className="space-y-6 text-lg text-gray-600 leading-relaxed" data-id="element-56">
                    <p data-id="element-57">{product.desc1}</p>
                    {product.desc2 && <p data-id="element-58">{product.desc2}</p>}

                    {product.feature && <div className="bg-lavender-50 p-6 rounded-xl border border-lavender-100 flex items-start gap-4 mt-8" data-id="element-59">
                        <div className="mt-1" data-id="element-60">{product.featureIcon}</div>
                        <p className="font-medium text-gray-800 italic" data-id="element-61">
                          {product.feature}
                        </p>
                      </div>}

                    {product.quoteQ && <div className="bg-white p-6 rounded-xl border border-lavender-100 shadow-sm mt-8 space-y-4" data-id="element-62">
                        <div className="flex items-start gap-3" data-id="element-63">
                          <MessageCircle className="w-5 h-5 text-lavender-400 mt-1 flex-shrink-0" data-id="element-64" />
                          <p className="text-gray-600 italic" data-id="element-65">
                            {product.questionLabel}{' '}
                            <span className="font-medium text-gray-800" data-id="element-66">
                              "{product.quoteQ}"
                            </span>
                          </p>
                        </div>
                        <div className="flex items-start gap-3" data-id="element-67">
                          <Sparkles className="w-5 h-5 text-lavender-600 mt-1 flex-shrink-0" data-id="element-68" />
                          <p className="text-gray-600 italic" data-id="element-69">
                            {product.answerLabel}{' '}
                            <span className="font-medium text-lavender-700" data-id="element-70">
                              "{product.quoteA}"
                            </span>
                          </p>
                        </div>
                      </div>}

                    {product.sets && <div className="space-y-4 mt-8" data-id="element-71">
                        {product.sets.map((set, i) => <div key={i} className="bg-white p-5 rounded-xl border border-blush-100 shadow-sm flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" data-id="element-72">
                            <span className="font-serif font-semibold text-blush-700 whitespace-nowrap" data-id="element-73">
                              {set.name}:
                            </span>
                            <span className="text-gray-600" data-id="element-74">{set.items}</span>
                          </div>)}
                      </div>}
                  </div>

                  <div className="mt-10" data-id="element-75">
                    <a href={buildShopUrl(product.productSlug)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-lavender-600 font-semibold hover:text-lavender-700 group" data-id="element-76">
                      {c.requestCta}
                      <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-77">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" data-id="element-78" />
                      </svg>
                    </a>
                  </div>
                </motion.div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}
function SingleImage({
  product,
  isEven
}: {
  product: Product;
  isEven: boolean;
}) {
  return <motion.div whileHover={{
    y: -6,
    rotate: isEven ? 1 : -1
  }} transition={{
    type: 'spring',
    stiffness: 200,
    damping: 18
  }} className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl border-[10px] border-white relative group bg-white" data-id="element-79">
      <div className="absolute inset-0 bg-gradient-to-tr from-lavender-500/10 via-transparent to-blush-300/10 group-hover:from-transparent group-hover:to-transparent transition-all duration-700 z-10 pointer-events-none" data-id="element-80"></div>

      <img src={product.image} alt={product.title} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-[1200ms] ease-out" data-id="element-81" />

      <motion.div animate={{
      y: [0, -10, 0],
      rotate: [0, 10, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-6 right-6 z-20" data-id="element-82">
        <div className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-md" data-id="element-83">
          <Sparkles className="w-5 h-5 text-gold-500" data-id="element-84" />
        </div>
      </motion.div>

      <motion.div animate={{
      y: [0, 8, 0],
      rotate: [0, -8, 0]
    }} transition={{
      duration: 5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1
    }} className="absolute bottom-6 left-6 z-20" data-id="element-85">
        <div className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-md" data-id="element-86">
          {product.icon}
        </div>
      </motion.div>

      <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden" data-id="element-87">
        <div className="absolute top-0 -left-full w-1/2 h-full bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12 group-hover:left-full transition-all duration-1000 ease-out" data-id="element-88"></div>
      </div>
    </motion.div>;
}
function TumblerFan({
  product,
  isEven
}: {
  product: Product;
  isEven: boolean;
}) {
  const gallery = product.gallery || [];
  const configs = [{
    rotate: -14,
    x: -110,
    y: 20,
    z: 1,
    dur: 4.5
  }, {
    rotate: -5,
    x: -38,
    y: -10,
    z: 3,
    dur: 5
  }, {
    rotate: 6,
    x: 38,
    y: 5,
    z: 2,
    dur: 4.2
  }, {
    rotate: 16,
    x: 110,
    y: 30,
    z: 1,
    dur: 4.8
  }];
  return <div className="relative aspect-[4/5] flex items-center justify-center" data-id="element-89">
      <div className="absolute inset-4 rounded-[2rem] bg-gradient-to-br from-lavender-100 via-cream-50 to-blush-100 shadow-inner" data-id="element-90"></div>

      <div className="absolute inset-0 pointer-events-none" data-id="element-91">
        {[...Array(8)].map((_, i) => <motion.div key={i} className="absolute w-2 h-2 rounded-full bg-white/80" style={{
        top: `${15 + i * 37 % 70}%`,
        left: `${10 + i * 53 % 80}%`
      }} animate={{
        opacity: [0.3, 0.9, 0.3],
        scale: [1, 1.4, 1]
      }} transition={{
        duration: 2 + i % 3,
        repeat: Infinity,
        delay: i * 0.2
      }} data-id="element-92" />)}
      </div>

      <div className="relative w-full h-full flex items-center justify-center" data-id="element-93">
        {gallery.map((src, i) => {
        const cfg = configs[i] || configs[0];
        return <motion.div key={i} initial={{
          opacity: 0,
          y: 40,
          rotate: cfg.rotate * 2
        }} whileInView={{
          opacity: 1,
          y: cfg.y,
          rotate: cfg.rotate
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.8,
          delay: 0.15 * i,
          ease: [0.22, 1, 0.36, 1]
        }} whileHover={{
          y: cfg.y - 16,
          scale: 1.06,
          zIndex: 50
        }} className="absolute" style={{
          zIndex: cfg.z,
          transform: `translateX(${cfg.x}px)`
        }} data-id="element-94">
              <motion.div animate={{
            y: [0, -8, 0]
          }} transition={{
            duration: cfg.dur,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.3
          }} className="relative" data-id="element-95">
                <div className="w-28 sm:w-32 md:w-36 lg:w-40 aspect-[1/2.4] rounded-2xl overflow-hidden bg-white shadow-2xl border-4 border-white relative group" data-id="element-96">
                  <img src={src} alt={`${product.title} ${i + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-97" />
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" data-id="element-98"></div>
                </div>
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-3/4 h-2 bg-black/15 rounded-full blur-md" data-id="element-99"></div>
              </motion.div>
            </motion.div>;
      })}
      </div>

      <motion.div animate={{
      y: [0, -10, 0],
      rotate: [0, 10, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-6 right-6 z-30" data-id="element-100">
        <div className="w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-md" data-id="element-101">
          <Sparkles className="w-5 h-5 text-gold-500" data-id="element-102" />
        </div>
      </motion.div>

      <motion.div animate={{
      y: [0, 8, 0],
      rotate: [0, -8, 0]
    }} transition={{
      duration: 5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1
    }} className="absolute bottom-6 left-6 z-30" data-id="element-103">
        <div className="w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-md" data-id="element-104">
          {product.icon}
        </div>
      </motion.div>

      <div className="absolute top-6 left-6 z-30" data-id="element-105">
        <div className="bg-white/95 backdrop-blur-sm rounded-full px-3 py-1.5 inline-flex items-center gap-2 shadow-md" data-id="element-106">
          <Coffee className="w-3.5 h-3.5 text-lavender-600" data-id="element-107" />
          <span className="text-xs font-medium text-gray-800" data-id="element-108">
            {product.tumblerBadgeText}
          </span>
        </div>
      </div>
    </div>;
}
function BowScatter({
  product,
  isEven
}: {
  product: Product;
  isEven: boolean;
}) {
  const gallery = (product.gallery || []).slice(0, 6);
  const configsByCount: Record<number, Array<{
    top: string;
    left: string;
    rotate: number;
    scale: number;
    z: number;
    dur: number;
    size: string;
  }>> = {
    3: [{
      top: '6%',
      left: '8%',
      rotate: -7,
      scale: 1,
      z: 2,
      dur: 4.5,
      size: 'w-[50%]'
    }, {
      top: '8%',
      left: '50%',
      rotate: 8,
      scale: 0.95,
      z: 1,
      dur: 5,
      size: 'w-[46%]'
    }, {
      top: '48%',
      left: '22%',
      rotate: -3,
      scale: 1.05,
      z: 4,
      dur: 4.8,
      size: 'w-[58%]'
    }],
    4: [{
      top: '4%',
      left: '6%',
      rotate: -8,
      scale: 1,
      z: 2,
      dur: 4.5,
      size: 'w-[46%]'
    }, {
      top: '6%',
      left: '52%',
      rotate: 7,
      scale: 0.95,
      z: 1,
      dur: 5,
      size: 'w-[44%]'
    }, {
      top: '52%',
      left: '4%',
      rotate: 6,
      scale: 0.95,
      z: 3,
      dur: 4.6,
      size: 'w-[44%]'
    }, {
      top: '54%',
      left: '50%',
      rotate: -5,
      scale: 1,
      z: 2,
      dur: 5.2,
      size: 'w-[46%]'
    }],
    6: [{
      top: '4%',
      left: '6%',
      rotate: -8,
      scale: 1.0,
      z: 2,
      dur: 4.5,
      size: 'w-[42%]'
    }, {
      top: '2%',
      left: '54%',
      rotate: 6,
      scale: 0.92,
      z: 1,
      dur: 5.2,
      size: 'w-[40%]'
    }, {
      top: '34%',
      left: '30%',
      rotate: -3,
      scale: 1.1,
      z: 4,
      dur: 4.8,
      size: 'w-[46%]'
    }, {
      top: '58%',
      left: '2%',
      rotate: 8,
      scale: 0.88,
      z: 2,
      dur: 5,
      size: 'w-[38%]'
    }, {
      top: '62%',
      left: '56%',
      rotate: -6,
      scale: 0.95,
      z: 3,
      dur: 4.2,
      size: 'w-[42%]'
    }, {
      top: '36%',
      left: '70%',
      rotate: 12,
      scale: 0.78,
      z: 1,
      dur: 5.5,
      size: 'w-[32%]'
    }]
  };
  const configs = configsByCount[gallery.length] || configsByCount[6];
  const badgeText = product.galleryBadge?.text ?? '';
  const badgeIcon = product.galleryBadge?.icon ?? <Ribbon className="w-3.5 h-3.5 text-gold-600" data-id="element-109" />;
  return <div className="relative aspect-[4/5] rounded-3xl overflow-hidden bg-gradient-to-br from-cream-50 via-blush-50 to-lavender-50 shadow-inner" data-id="element-110">
      <div className="absolute inset-0 pointer-events-none" data-id="element-111">
        {[...Array(10)].map((_, i) => <motion.div key={i} className="absolute rounded-full bg-white shadow-sm" style={{
        width: `${4 + i % 3 * 2}px`,
        height: `${4 + i % 3 * 2}px`,
        top: `${i * 41 % 95}%`,
        left: `${i * 67 % 95}%`
      }} animate={{
        opacity: [0.4, 0.95, 0.4],
        scale: [1, 1.3, 1]
      }} transition={{
        duration: 2.5 + i % 3,
        repeat: Infinity,
        delay: i * 0.18
      }} data-id="element-112" />)}
      </div>

      {gallery.map((src, i) => {
      const cfg = configs[i] || configs[0];
      return <motion.div key={i} initial={{
        opacity: 0,
        scale: 0.6,
        rotate: cfg.rotate * 2
      }} whileInView={{
        opacity: 1,
        scale: cfg.scale,
        rotate: cfg.rotate
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.7,
        delay: 0.12 * i,
        ease: [0.22, 1, 0.36, 1]
      }} whileHover={{
        scale: cfg.scale * 1.08,
        rotate: 0,
        zIndex: 50
      }} className={`absolute ${cfg.size}`} style={{
        top: cfg.top,
        left: cfg.left,
        zIndex: cfg.z
      }} data-id="element-113">
            <motion.div animate={{
          y: [0, -6, 0]
        }} transition={{
          duration: cfg.dur,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: i * 0.25
        }} className="relative" data-id="element-114">
              <div className="aspect-square rounded-2xl overflow-hidden bg-white shadow-xl border-4 border-white group" data-id="element-115">
                <img src={src} alt={`${product.title} ${i + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-116" />
                <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-117">
                  <div className="absolute top-0 -left-full w-1/2 h-full bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-12 group-hover:left-full transition-all duration-1000" data-id="element-118"></div>
                </div>
              </div>
            </motion.div>
          </motion.div>;
    })}

      <motion.div animate={{
      y: [0, -10, 0],
      rotate: [0, 10, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-4 right-4 z-30" data-id="element-119">
        <div className="w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-md" data-id="element-120">
          <Sparkles className="w-5 h-5 text-gold-500" data-id="element-121" />
        </div>
      </motion.div>

      <motion.div animate={{
      y: [0, 8, 0],
      rotate: [0, -8, 0]
    }} transition={{
      duration: 5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1
    }} className="absolute bottom-4 right-4 z-30" data-id="element-122">
        <div className="w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-md" data-id="element-123">
          {product.icon}
        </div>
      </motion.div>

      <div className="absolute bottom-4 left-4 z-30" data-id="element-124">
        <div className="bg-white/95 backdrop-blur-sm rounded-full px-3 py-1.5 inline-flex items-center gap-2 shadow-md" data-id="element-125">
          {badgeIcon}
          <span className="text-xs font-medium text-gray-800" data-id="element-126">{badgeText}</span>
        </div>
      </div>
    </div>;
}